import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import {
    Box
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import CategoryIcon from "@mui/icons-material/Category";
import CampaignIcon from "@mui/icons-material/Campaign";
import ExploreIcon from "@mui/icons-material/Explore";
import SettingsIcon from "@mui/icons-material/Settings";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";

import Home from "../pages/Home";
import UserList from "../pages/UserList";
import DriverList from "../pages/DriverList";
import LoginPage from "../pages/LoginPage";
import PrivateRoute from "../components/PrivateRoute";
import { logout } from "../services/authService";
import { isAuthenticated } from "../utils/auth";
import RideList from "../pages/RideList";
import RichNavDrawer from "../components/RichNavDrawer";
import AppConfigList from "../pages/AppConfigList";
import RideOptionList from "../pages/RideOptionList";
import MarketingList from "../pages/MarketingList";
import TabbedSectionAdmin from "../pages/TabbedSectionAdmin";
import WalletList from "../pages/WalletList";
import SupportTicketList from "../pages/SupportTicketList";
import SupportTicketDetail from "../pages/SupportTicketDetail";
import RideDetail from "../pages/RideDetail";
import UserDetail from "../pages/UserDetail";
import DriverDetail from "../pages/DriverDetail";
import NotificationComposer from "../pages/NotificationComposer";
import SubscriptionPlans from "../pages/SubscriptionPlans";
import BusinessSettings from "../pages/BusinessSettings";
import ZoneExplorer from "../pages/ZoneExplorer";
import ZoneDetails from "../pages/ZoneDetails";

const drawerWidth = 235;

// Notice: `icon` is now a component type, NOT JSX!
const menuItems = [
    { text: "Dashboard", icon: DashboardIcon, path: "/", color: "#24b9d4" },
    { text: "Support Tickets", icon: SupportAgentIcon, path: "/support-tickets", color: "#24b9d4" },
    { text: "Users", icon: PeopleIcon, path: "/users", color: "#24b9d4" },
    { text: "Drivers", icon: PersonIcon, path: "/drivers", color: "#24b9d4" },
    { text: "Rides", icon: LocalTaxiIcon, path: "/rides", color: "#24b9d4" },
    { text: "Ride Option", icon: DriveEtaIcon, path: "/rides-options", color: "#f59e0b" },
    { text: "Zones", icon: MapIcon, path: "/zones", color: "#f59e0b" },
    {
        text: "System Health",
        icon: TuneIcon,
        path: "/queues",
        isExternal: true,
        color: "#ef4444"
    },
    { text: "Push Notifications", icon: NotificationsActiveIcon, path: "/notifications/send", color: "#8b5cf6" },

    {
        text: "Categories",
        icon: CategoryIcon,
        color: "#8b5cf6",
        children: [
            { text: "Marketing", icon: CampaignIcon, path: "/marketing", color: "#8b5cf6" },
            { text: "Explore Items", icon: ExploreIcon, path: "/explore-list", color: "#8b5cf6" },
        ],
    },

    {
        text: "Settings",
        icon: SettingsIcon,
        color: "#94a3b8",
        children: [
            { text: "App Config", icon: AppSettingsAltIcon, path: "/app-config", color: "#94a3b8" },
            { text: "Revenue Plans", icon: MonetizationOnIcon, path: "/subscription-plans", color: "#f59e0b" },
            { text: "Business Logic", icon: TuneIcon, path: "/business-settings", color: "#94a3b8" },
        ],
    },
];


const AppRoutes: React.FC = () => {
    const location = useLocation();
    const showDrawer = isAuthenticated() && location.pathname !== "/login";

    return (
        <Box sx={{ display: "flex" }}>
            {showDrawer && (
                <RichNavDrawer
                    menuItems={menuItems}
                    onLogout={logout}
                    drawerWidth={drawerWidth}
                />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    px: { xs: 1, sm: 3, md: 4 },
                    py: { xs: 2, sm: 3 },
                    minHeight: '100vh',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: "#f5f7fa"
                }}
            >
                <Routes>
                    <Route path="/login" element={<LoginPage />} />

                    {/* Core Routes */}
                    <Route element={<PrivateRoute><Home /></PrivateRoute>} path="/" />
                    <Route element={<PrivateRoute><UserList /></PrivateRoute>} path="/users" />
                    <Route element={<PrivateRoute><UserDetail /></PrivateRoute>} path="/user/:id" />
                    <Route element={<PrivateRoute><DriverList /></PrivateRoute>} path="/drivers" />
                    <Route element={<PrivateRoute><DriverDetail /></PrivateRoute>} path="/driver/:id" />
                    <Route element={<PrivateRoute><RideList /></PrivateRoute>} path="/rides" />
                    <Route element={<PrivateRoute><RideDetail /></PrivateRoute>} path="/ride/:id" />
                    <Route element={<PrivateRoute><WalletList /></PrivateRoute>} path="/wallet-transactions" />

                    {/* Management Routes */}
                    <Route element={<PrivateRoute><RideOptionList /></PrivateRoute>} path="/rides-options" />
                    <Route element={<PrivateRoute><ZoneExplorer /></PrivateRoute>} path="/zones" />
                    <Route element={<PrivateRoute><ZoneDetails /></PrivateRoute>} path="/zones/:id" />
                    <Route element={<PrivateRoute><MarketingList /></PrivateRoute>} path="/marketing" />
                    <Route element={<PrivateRoute><TabbedSectionAdmin /></PrivateRoute>} path="/explore-list" />
                    <Route element={<PrivateRoute><SupportTicketList /></PrivateRoute>} path="/support-tickets" />
                    <Route element={<PrivateRoute><SupportTicketDetail /></PrivateRoute>} path="/support-ticket/:id" />
                    <Route element={<PrivateRoute><NotificationComposer /></PrivateRoute>} path="/notifications/send" />

                    {/* Settings Routes */}
                    <Route element={<PrivateRoute><AppConfigList /></PrivateRoute>} path="/app-config" />
                    <Route element={<PrivateRoute><SubscriptionPlans /></PrivateRoute>} path="/subscription-plans" />
                    <Route element={<PrivateRoute><BusinessSettings /></PrivateRoute>} path="/business-settings" />
                </Routes>
            </Box>
        </Box>
    );
};

export default AppRoutes;